import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router";

import type { CardProfileType } from "src/app/features/ApplicationProfile/subFeatures/CardProfile/types/CardProfileType";
import { FormManager } from "../../../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../components/Form/types/FormProps";
import { ResourceName } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../../../core/setup/redux/state/notifications/notificationsState";
import { usePromptDataSaveMutation } from "../../services/server/PromptDataApi";
import type { PromptDataQueryParams } from "../../services/server/PromptDataQueryTypes";
import type { PromptDataEntity } from "../../types/PromptDataEntity";

export const PromptDataCreateForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("prompt-data");
    const { appProfileId, cardProfId } = useParams();
    const [searchParams] = useSearchParams();
    const type = searchParams.get("type") as CardProfileType;

    const [queryConfig, setQueryConfig] = useState<PromptDataQueryParams>({} as PromptDataQueryParams);

    useEffect(() => {
        if (appProfileId && cardProfId) {
            setQueryConfig({ appProfileId, cardProfId });
        }
    }, [appProfileId, cardProfId]);

    const [formData, setFormData] = useState<PromptDataEntity>({} as PromptDataEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [saveFormData, { isLoading: saveFormIsInProgress }] = usePromptDataSaveMutation();

    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData || !queryConfig) {
                return;
            }

            const result = await saveFormData({ promptDataFormData: formData, promptDataQuery: queryConfig });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formSaveSuccess", {
                            id: result.data.id,
                            resource: "PromptData",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(
                    `${BASE_PATH}/${ResourceName.APPLICATION_PROFILE_LISTS}/${appProfileId}/${ResourceName.APPLICATION_PROFILE_CARD_DEFINITION_EFT4F}/${FORM_ACTION.EDIT}/${cardProfId}?type=${type}`,
                );
            }
        },
        [formData, queryConfig, saveFormData, dispatch, navigate, appProfileId, cardProfId, type],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<PromptDataEntity>
                    name="PromptDataCreateForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
